/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Spacer,
  Flex,
  useMediaQuery,
  chakra,
  IconButton,
  Box,
} from '@chakra-ui/react';
import Text from '@molecules/Text';
import styled from '@emotion/styled';
import CreditCardForm from '@view/components/CreditCards/CreditCardForm';
import ScreenSize from '../../../constants/screen-sizes';
import { ReactComponent as AccordionOnIcon } from '@view/assets/icons/accordion_on.svg';
import { ReactComponent as AccordionOffIcon } from '@view/assets/icons/accordion_off.svg';
import { ReactComponent as PaypalGiantIcon } from '@view/assets/icons/payment/cc_paypal_giant.svg';
import { ReactComponent as PaypalIcon } from '@view/assets/icons/payment/cc_paypal_default.svg';
import { ReactComponent as BankIcon } from '@view/assets/icons/bank.svg';
import { useStore } from '@data/other/withStore';
import { PAYMENT_METHOD } from '@data/models/Payment';
import PaymentModel from '@data/models/Payment';
import { useState, useCallback, useEffect } from 'react';
import Card from '@molecules/Card';
import BEIcon from '@molecules/BEIcon';
import { useNavigate, useParams } from 'react-router-dom';
import { Routes as AppRoutes } from '@view/routes';
import ShoppingCart from '@view/components/Cards/ShoppingCart';
import useForm from '@molecules/FormFactory/useForm';
import CreditCard from '@data/models/CreditCard';
import useAsyncAction from '@data/hooks/useAsyncAction';
import { Guest, Property, ReservationResponse } from '@data/models';
import { observer } from 'mobx-react';
import { useAlert } from '@molecules/BEOffScreen/Modal';
import { useTranslation } from 'react-i18next';
import ReservationItem from '@data/models/ReservationItem';
import CreditCardLogoTile from '@view/components/CreditCards/CreditCardLogoTile';
import { CARD_TYPES } from '@business/utils/creditcard';
import { usePageNavigation } from '@view/hooks/usePageNavigation';
import { useModal } from '@molecules/BEOffScreen';
import ModalCancelationPolicy from '@view/components/Modals/CancelationPolicy';

interface AccordionButtonProps {
  isExpanded?: boolean;
}

const Payment = () => {
  const navigate = usePageNavigation();
  const { t } = useTranslation();
  const { propertyCode } = useParams();

  const { property } = useStore();
  const { cart, guests, payment, reservation } = property;
  const { goTo } = usePageNavigation();

  const {
    bankTransfersEnabled,
    creditCardsEnabled,
    paypalEnabled,
    supportedCardTypes,
  } = property.model?.paymentMethods || {};
  const { requireCvv = true } = property.model?.settings || {};
  const { email, phone } = property.model?.contact || {};

  const orderedMethods: PAYMENT_METHOD[] = ['cards', 'pay_pal', 'ebanking'];
  const methodsAndAvailability = {
    cards: creditCardsEnabled,
    pay_pal: paypalEnabled,
    ebanking: bankTransfersEnabled,
  };
  const availablePaymentMethods: PAYMENT_METHOD[] = orderedMethods.filter(
    (method) => methodsAndAvailability[method]
  );

  const defaultMethod = availablePaymentMethods[0];
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<
    PAYMENT_METHOD | undefined
  >(payment.model.method || defaultMethod);

  useEffect(() => {
    if (!payment.model.method && defaultMethod) {
      payment.setMethod(defaultMethod);
    }
  }, [defaultMethod]);

  const [isMobile] = useMediaQuery(`(max-width: ${ScreenSize.MOBILE_LARGE})`);

  const handlePaymentMethodChange = (index) => {
    const method: PAYMENT_METHOD = availablePaymentMethods[index];
    setSelectedPaymentMethod(method);
    payment.setMethod(method);
  };

  const useFormResult = useForm(async (data: CreditCard) => {
    const doSubmit = async () => {
      if (
        !property.model ||
        cart.items.length < 1 ||
        !payment.model ||
        !cart.checkIn ||
        !cart.checkOut
      )
        return;
      payment.setCreditCard(data);
      await makePayment(
        property.model,
        cart.items,
        cart.checkIn,
        cart.checkOut,
        [guests.main, ...guests.additional],
        payment.model
      );
    };
    if (property.model?.settings?.requireAcceptTerms) {
      cancelationPolicyModal.open({
        onAgree: async () => {
          await doSubmit();
        },
      });
    } else {
      await doSubmit();
    }
  });

  const { onSave: onCreditSubmit } = useFormResult;

  const [makePayment, , { error }] = useAsyncAction<
    ReservationResponse,
    [
      property: Property,
      items: ReservationItem[],
      checkin: Date,
      checkout: Date,
      guests: Guest[],
      payment: PaymentModel
    ]
  >(
    useCallback(
      async (...params) => await reservation.make(...params),
      [reservation]
    ),
    useCallback(() => {
      navigate.goTo(AppRoutes.ReservationConfirmed);
    }, [navigate])
  );

  const alert = useAlert();
  useEffect(() => {
    if (error) {
      alert({
        title: 'Payment Failed',
        content: t('payment-error', { email, phone }) as string,
        showOk: false,
        showCancel: false,
      });
    }
  }, [t, error, alert, email, phone]);

  const cancelationPolicyModal = useModal(ModalCancelationPolicy, {
    width: 560,
  });

  const onSubmit = useCallback(async () => {
    if (selectedPaymentMethod === 'cards') {
      await onCreditSubmit();
    } else {
      const doSubmit = async () => {
        if (
          !property.model ||
          cart.items.length < 1 ||
          !payment.model ||
          !cart.checkIn ||
          !cart.checkOut
        )
          return;
        await makePayment(
          property.model,
          cart.items,
          cart.checkIn,
          cart.checkOut,
          [guests.main, ...guests.additional],
          payment.model
        );
      };
      if (property.model?.settings?.requireAcceptTerms) {
        cancelationPolicyModal.open({
          onAgree: async () => {
            await doSubmit();
          },
        });
      } else {
        await doSubmit();
      }
    }
  }, [
    selectedPaymentMethod,
    onCreditSubmit,
    property.model,
    cart.items,
    cart.checkIn,
    cart.checkOut,
    payment.model,
    makePayment,
    guests.main,
    guests.additional,
    cancelationPolicyModal,
  ]);

  return (
    <StyledWrapper>
      <Card>
        <StyledHeader>
          <StyledBackButton
            icon={<BEIcon name="arrow-left" size="sm" />}
            onClick={() => goTo(AppRoutes.AddGuest, AppRoutes.Payment)}
            aria-label={''}
          />
          <Text type="h1">Payment</Text>
        </StyledHeader>
        <Accordion
          defaultIndex={
            selectedPaymentMethod
              ? availablePaymentMethods.indexOf(selectedPaymentMethod)
              : -1
          }
          pt={6}
          pb={10}
          onChange={handlePaymentMethodChange}
        >
          {creditCardsEnabled && (
            <>
              <StyledAccordianItem isDisabled={false}>
                {({ isExpanded }) => (
                  <>
                    <h2>
                      <StyledAccordianButton isExpanded={isExpanded}>
                        {isExpanded ? (
                          <AccordionOnIcon />
                        ) : (
                          <AccordionOffIcon />
                        )}
                        <StyledTitle size="lg" weight="600">
                          Credit Card
                        </StyledTitle>
                        <Spacer />
                        <CreditCardLogoTile
                          isMobile={isMobile}
                          supportedCards={supportedCardTypes || []}
                        />
                      </StyledAccordianButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <CreditCardForm
                        requireCvv={requireCvv}
                        defaultValues={payment.creditCard}
                        useFormResult={useFormResult}
                      />
                    </AccordionPanel>
                  </>
                )}
              </StyledAccordianItem>
              <Spacer height={6} />
            </>
          )}
          {paypalEnabled && (
            <>
              <StyledAccordianItem isDisabled={false}>
                {({ isExpanded }) => (
                  <>
                    <h2>
                      <StyledAccordianButton
                        isExpanded={isExpanded}
                        alignItems="center"
                      >
                        {isExpanded ? (
                          <AccordionOnIcon />
                        ) : (
                          <AccordionOffIcon />
                        )}
                        <StyledTitle size="lg" weight="600">
                          PayPal
                        </StyledTitle>
                        <Spacer />
                        {isMobile ? <PaypalIcon /> : <PaypalGiantIcon />}
                      </StyledAccordianButton>
                    </h2>
                    <AccordionPanel pb={4}>
                      <Text>
                        You will be redirected to PayPal payment screen after
                        confirming the reservation
                      </Text>
                    </AccordionPanel>
                  </>
                )}
              </StyledAccordianItem>
              <Spacer height={6} />
            </>
          )}
          {bankTransfersEnabled && (
            <StyledAccordianItem>
              {({ isExpanded }) => (
                <>
                  <h2>
                    <StyledAccordianButton isExpanded={isExpanded}>
                      {isExpanded ? <AccordionOnIcon /> : <AccordionOffIcon />}
                      <StyledTitle size="lg" weight="600">
                        Bank Transfer
                      </StyledTitle>
                      <Spacer />
                      <StyledBankIconWrapper
                        justifyContent="center"
                        alignItems="center"
                      >
                        <BankIcon />
                      </StyledBankIconWrapper>
                    </StyledAccordianButton>
                  </h2>
                  <AccordionPanel pb={4}>
                    <Text>
                      You will be provided transfer instructions after
                      confirming the reservation
                    </Text>
                  </AccordionPanel>
                </>
              )}
            </StyledAccordianItem>
          )}
        </Accordion>
      </Card>
      <StyledSideComponent>
        <ShoppingCart path={AppRoutes.Payment} onOk={onSubmit} />
      </StyledSideComponent>
    </StyledWrapper>
  );
};

export const StyledWrapper = styled.div`
  display: flex;
  flex-direction: row;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    flex-direction: column;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  gap: 16px;
  justify-content: start;
  align-items: center;
`;

const StyledBackButton = chakra(IconButton, {
  baseStyle: {
    borderWidth: '1px',
    borderRadius: '8px',
    borderColor: 'neutral.500',
    width: '40px',
    height: '40px',
    backgroundColor: 'white',
    margin: '12px 0px',
  },
});

const StyledSideComponent = styled(Box)`
  position: relative;
  display:flex;
  flex-direction: column;

  width: 340px;
  min-width: 340px;
  margin-left: 40px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    width: 100%;
    margin-left: unset;
  }
`;

const StyledAccordianItem = styled(AccordionItem)`
  background: #F8F9FA;
  border: 1px solid #DDE0E4;
  border-radius: 16px;
`;
const StyledAccordianButton = styled(AccordionButton)<AccordionButtonProps>`
  padding: 16px;
  border-radius: 16px;
  border-bottom-left-radius: ${(props) => (props.isExpanded ? 0 : '16px')};
  border-bottom-right-radius: ${(props) => (props.isExpanded ? 0 : '16px')};
`;
const StyledTitle = styled(Text)`
  padding-left: 8px;
`;
const StyledBankIconWrapper = styled(Flex)`
  width: 58px;
  height: 40px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    width: 46px;
    height: 32px;
  }
`;
const StyledSpacer = styled.div`
  width: 16px;

  @media screen and (max-width: ${ScreenSize.MOBILE_LARGE}) {
    width: 4px;
  }
`;

export default observer(Payment);
